/**
 * Camera control utilities for neural network visualization
 */
export class CameraController {
  private mouseState = {
    current: { x: 0, y: 0 },
    target: { x: 0, y: 0 },
    isActive: false,
    timeout: null as any,
  }

  constructor(
    private THREE: any,
    private camera: any,
    private network: any,
  ) {}

  private _getNetworkCenter(): any {
    if (
      !this.network ||
      !this.network.neurons ||
      this.network.neurons.length === 0
    ) {
      return new this.THREE.Vector3(0, 0, 0)
    }

    const center = new this.THREE.Vector3()
    for (const neuron of this.network.neurons) {
      center.add(neuron.position)
    }
    center.divideScalar(this.network.neurons.length)
    return center
  }

  /**
   * Update mouse state from event
   */
  updateMousePosition(x: number, y: number): void {
    this.mouseState.target.x = x
    this.mouseState.target.y = y
    this.mouseState.isActive = true

    if (this.mouseState.timeout) {
      clearTimeout(this.mouseState.timeout)
    }

    this.mouseState.timeout = setTimeout(() => {
      this.mouseState.isActive = false
    }, 3000)
  }

  /**
   * Deactivate mouse control
   */
  deactivateMouseControl(): void {
    this.mouseState.isActive = false
    if (this.mouseState.timeout) {
      clearTimeout(this.mouseState.timeout)
    }
  }

  /**
   * Update camera position with smooth interpolation
   */
  updateCamera(elapsedTime: number): void {
    const smoothing = 0.05
    this.mouseState.current.x +=
      (this.mouseState.target.x - this.mouseState.current.x) * smoothing
    this.mouseState.current.y +=
      (this.mouseState.target.y - this.mouseState.current.y) * smoothing

    if (this.mouseState.isActive) {
      this.updateInteractiveCamera(elapsedTime)
    } else {
      this.updateCinematicCamera(elapsedTime)
    }
  }

  /**
   * Interactive mouse-controlled camera movement
   */
  private updateInteractiveCamera(elapsedTime: number): void {
    const mouseInfluence = 150
    const verticalInfluence = 100
    const depthInfluence = 80

    const slowDrift = elapsedTime * 0.02
    const baseX = Math.sin(slowDrift * 0.3) * 20
    const baseY = Math.cos(slowDrift * 0.4) * 15
    const baseZ = 50 + Math.sin(slowDrift * 0.2) * 30

    this.camera.position.x = baseX + this.mouseState.current.x * mouseInfluence
    this.camera.position.y =
      baseY + this.mouseState.current.y * verticalInfluence
    this.camera.position.z = baseZ + this.mouseState.current.y * depthInfluence

    const networkCenter = this._getNetworkCenter()
    this.camera.lookAt(networkCenter)

    const bankAngle = this.mouseState.current.x * 0.1
    this.camera.rotateZ(bankAngle)
  }

  /**
   * Cinematic flythrough camera movement
   */
  private updateCinematicCamera(elapsedTime: number): void {
    const speed = 0.12
    const amplitude = 120
    const verticalRange = 80
    const depthRange = 150

    const t = elapsedTime * speed

    this.camera.position.x =
      Math.sin(t * 0.7) * amplitude +
      Math.sin(t * 2.3) * amplitude * 0.4 +
      Math.cos(t * 1.1) * amplitude * 0.6

    this.camera.position.y =
      Math.cos(t * 0.9) * verticalRange +
      Math.sin(t * 1.8) * verticalRange * 0.5 +
      Math.cos(t * 2.7) * verticalRange * 0.3

    this.camera.position.z =
      50 +
      Math.sin(t * 0.4) * depthRange +
      Math.cos(t * 1.2) * depthRange * 0.4 +
      Math.sin(t * 0.8) * depthRange * 0.3

    const networkCenter = this._getNetworkCenter()
    this.camera.lookAt(networkCenter)
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    if (this.mouseState.timeout) {
      clearTimeout(this.mouseState.timeout)
    }
  }
}
